/* eslint-disable @typescript-eslint/naming-convention */
import { IPrismicParagraph, parseTextParagraphs } from 'stores/prismicHelpers'
import { beautifySubcontentTitle } from 'utils/string'
import * as PrismicDOM from 'prismic-dom'
import { IImageData } from '../../subcontent_modules/subcontentInterfaces'
import { Dimensions, ResponsiveUrl } from 'stores/contentStore/contentTypes'

export interface IWithUrl {
  url: string
  alt?: string
}

export interface DefaultBasePrismicSubcontentData {
  title?: unknown
  secondary_title?: unknown
  thumbnail?: IWithUrl
  is_overlay?: boolean
}

// Video
export interface IPrismicSubcontentVideoData
  extends DefaultBasePrismicSubcontentData {
  duration?: unknown
  mobile_url_video?: IWithUrl
  desktop_url_video?: IWithUrl
  description: unknown
  poster?: IResponsiveUrlPrismicData
  video_url_ie_11?: IWithUrl
}
export interface IPrismicSubcontentVideo {
  id: string
  uid: string
  data: IPrismicSubcontentVideoData
}

// Text

export interface IPrismicSubcontentTextData
  extends DefaultBasePrismicSubcontentData {
  lead_text: unknown
  background_image?: IWithUrl
  image_caption: unknown
  body: Array<{ items: IPrismicParagraph[] }>
}

export interface IPrismicSubcontentText {
  id: string
  uid: string
  data: IPrismicSubcontentTextData
}

// Image

export interface IPrismicSubcontentImageData
  extends DefaultBasePrismicSubcontentData {
  background_image?: IWithUrl
  image_description: unknown
}

export interface IPrismicSubcontentImage {
  id: string
  uid: string
  data: IPrismicSubcontentImageData
}

// Scrollytelling

export interface IPrismicSubcontentScrollytellingData
  extends DefaultBasePrismicSubcontentData {
  background_image?: IWithUrl
  infographics_file: IWithUrl
  fallback_nonwebp: IWithUrl
  portrait_yaml: IWithUrl
  portrait_yaml_nonwebp: IWithUrl
  animation_background: IWithUrl
  interaction_description: unknown
  height_of_section: number
  height_of_section_mobile: number
}

export interface IPrismicSubcontentScrollytelling {
  id: string
  uid: string
  data: IPrismicSubcontentScrollytellingData
}

// Graphics

export interface IPrismicSubcontentGraphicsData
  extends DefaultBasePrismicSubcontentData {
  background_image?: IWithUrl
  optional_graphics_image: IWithUrl
  link_to_interactive_file: IWithUrl
  interaction_description: unknown
}

export interface IPrismicSubcontentGraphics {
  id: string
  uid: string
  data: IPrismicSubcontentGraphicsData
}

// Gallery

export interface IPrismicSubcontentGalleryData
  extends DefaultBasePrismicSubcontentData {
  body: IImageSlice[]
}

export interface IPrismicSubcontentGallery {
  id: string
  uid: string
  data: IPrismicSubcontentGalleryData
}

interface IImageSlice {
  items: Array<{
    image: IWithUrl
    image_description: string
    image_title: string
  }>
}

// Quizzes

export interface IPrismicQuizzes {
  id?: string
  uid?: string
  data: {
    background?: IWithUrl
    title?: unknown
    quizzes?: Array<{
      link_to_quiz: {
        id: string
      }
    }>
  }
}

export const prismicDataParser = {
  video: (subcontent: IPrismicSubcontentVideo) => {
    if (!subcontent || !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id ?? '',
      uid: subcontent.uid ?? '',
      type: 'video',
      title:
        (data.title &&
          beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title))) ??
        '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ??
        '',
      duration:
        (data.duration && PrismicDOM.RichText.asText(data.duration)) ?? '',
      thumbnail: data.thumbnail?.url ?? '',
      mobileVideoUrl: data.mobile_url_video?.url ?? '',
      desktopVideoUrl: data.desktop_url_video?.url ?? '',
      description:
        (data.description && PrismicDOM.RichText.asHtml(data.description)) ??
        '',
      poster: data.poster?.url ?? '',
      ieVideoUrl: data.video_url_ie_11?.url ?? '',
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  text: (subcontent: IPrismicSubcontentText) => {
    if (!subcontent ?? !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id ?? '',
      uid: subcontent.uid ?? '',
      type: 'text',
      title:
        beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title)) ?? '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ??
        '',
      thumbnail: data.thumbnail?.url ?? '',
      leadText: PrismicDOM.RichText.asHtml(data.lead_text) ?? '',
      backgroundImage: data.background_image?.url ?? '',
      imageCaption: PrismicDOM.RichText.asText(data.image_caption) ?? '',
      textContent: parseTextParagraphs(data.body[0]?.items),
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  image: (subcontent: IPrismicSubcontentImage) => {
    if (!subcontent || !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id || '',
      uid: subcontent.uid || '',
      type: 'image',
      title:
        beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title)) || '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ||
        '',
      thumbnail: data.background_image?.url ?? '',
      description: PrismicDOM.RichText.asText(data.image_description) || '',
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  scrollytelling: (subcontent: IPrismicSubcontentScrollytelling) => {
    if (!subcontent || !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id || '',
      uid: subcontent.uid || '',
      type: 'scrollytelling',
      title:
        beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title)) || '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ||
        '',
      thumbnail: data.thumbnail?.url ?? '',
      infoGraphicFile: data.infographics_file?.url || '',
      fallbackNonWebP: data.fallback_nonwebp?.url || '',
      portraitYaml: data.portrait_yaml?.url || '',
      portraitYamlNonWebP: data.portrait_yaml_nonwebp?.url || '',
      animationBackgroundImage: data.animation_background?.url || '',
      interactionDescription:
        PrismicDOM.RichText.asText(data.interaction_description) || '',
      heightOfSection: data.height_of_section || 1.5,
      heightOfSectionMobile:
        data.height_of_section_mobile || data.height_of_section_mobile || 2,
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  graphics: (subcontent: IPrismicSubcontentGraphics) => {
    if (!subcontent || !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id || '',
      uid: subcontent.uid || '',
      type: 'graphics',
      title:
        beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title)) || '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ||
        '',
      thumbnail: data.thumbnail?.url ?? '',
      optionalImage: data.optional_graphics_image?.url || '',
      linkToInteractiveFile: data.link_to_interactive_file?.url || '',
      interactionDescription:
        PrismicDOM.RichText.asText(data.interaction_description) || '',
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  gallery: (subcontent: IPrismicSubcontentGallery) => {
    if (!subcontent || !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id || '',
      uid: subcontent.uid || '',
      type: 'gallery',
      title:
        (data.title &&
          beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title))) ||
        '',
      secondaryTitle:
        (data.secondary_title &&
          PrismicDOM.RichText.asText(data.secondary_title)) ||
        '',
      thumbnail: data.thumbnail?.url ?? '',
      imagesCount: parseImages(data.body).count,
      images: parseImages(data.body).images || [],
      openAsOverlay: data.is_overlay ?? false,
    }
  },
  quizzes: (subcontent: IPrismicQuizzes) => {
    if (!subcontent ?? !subcontent.data) {
      return
    }
    const data = subcontent.data
    return {
      id: subcontent.id ?? '',
      type: 'quizzes',
      uid: subcontent.uid ?? '',
      thumbnail: data.background?.url ?? '',
      title:
        beautifySubcontentTitle(PrismicDOM.RichText.asText(data.title)) ??
        'Quiz',
      quizzesIds:
        data.quizzes?.map((quizItem) => quizItem.link_to_quiz.id) ?? [],
    }
  },
}

const parseImages = (imageSlices: IImageSlice[] = []) => {
  const images: IImageData[] = []
  let count = 0
  for (const imageSlice of imageSlices) {
    for (const imageData of imageSlice.items) {
      images.push({
        url: imageData.image?.url || '',
        description:
          (imageData.image_description &&
            PrismicDOM.RichText.asText(imageData.image_description)) ||
          '',
        title:
          (imageData.image_title &&
            PrismicDOM.RichText.asText(imageData.image_title)) ||
          '',
      })
      count++
    }
  }
  return {
    images,
    count,
  }
}

interface IResolutionPrismicData {
  dimensions: Dimensions
  url: string
  alt?: string
}

export interface IResponsiveUrlPrismicData extends IWithUrl {
  alt?: string
  copyright: string
  url: string
  dimensions: Dimensions
  [resolution: string]: undefined | string | Dimensions | IResolutionPrismicData
}

export function resolutionDataIsObject(
  input: undefined | string | Dimensions | IResolutionPrismicData
): input is IResolutionPrismicData {
  return !!(input as IResolutionPrismicData)?.url
}

export const parseResponsiveUrls = (data: IResponsiveUrlPrismicData) => {
  const result: { [id: string]: ResponsiveUrl } = {}
  const defaultFields = ['dimensions', 'alt', 'copyright', 'url']
  for (const resolution in data) {
    const resolutionData = data[resolution]
    if (
      defaultFields.indexOf(resolution) < 0 &&
      resolutionDataIsObject(resolutionData)
    ) {
      result[resolution] = {
        dimensions: resolutionData.dimensions,
        url: resolutionData.url,
        alt: resolutionData.alt,
      }
    }
  }
  return result
}
